import { createActions, createReducer } from "reduxsauce"
import { call, put, takeLatest, select } from "redux-saga/effects"
import { chain, filter, omit, some } from "lodash"
import { patch as apiPatch, post as apiPost, del as apiDelete } from "api"
import { Creators as notificationCreators } from "modules/notifications/model"
import { Types as UpdaterTypes } from "model/updater"
import { updateUser } from "../../../actions/user"
import { Types as HistoryTypes } from "../../../actions/app/history"
import { getCurrentDeviceId } from "./selectors"

export const { Types, Creators } = createActions({
  setAuthType: ["data"],
  setMfaSecret: ["qrCode", "secret"],
  resetMfa: [],
  newMfaDevice: ["token"],
  addMfaDevice: ["device", "qrCode"],
  deleteMfaDevice: ["deviceId", "token"],
  verifyMfaDevice: ["deviceName", "token"],
  saveMfaDeviceName: ["name", "deviceId"],
  setNewDeviceName: ["newDeviceName"],
}, {
  prefix: "ompnt/modules/user-profile/",
})

const DEFAULT_STATE = {
  profileErrors: [],
  passwordErrors: [],
  authType: {},
  mfaDevices: [],
  currentDeviceId: null,
  mfaSecret: null,
  qrCode: null,
  newDeviceName: null,
}

function handleUpdateError(state, action) {
  if (action.resource === "profile") {
    if (action.error.code === 400) {
      return { ...state, profileErrors: action.error.errors.map(error => error.message) }
    } else {
      return { ...state, profileErrors: ["A technical error has happened. Please try again later."] }
    }
  } else if (action.resource === "password") {
    if (action.error.code === 400) {
      return { ...state, passwordErrors: action.error.errors.map(error => error.message) }
    } else {
      return { ...state, passwordErrors: ["A technical error has happened. Please try again later."] }
    }
  } else {
    return state
  }
}

function handleUpdateSuccess(state, action) {
  if (action.resource === "profile") {
    return { ...state, profileErrors: [] }
  } else if (action.resource === "password") {
    return { ...state, passwordErrors: [] }
  } else {
    return state
  }
}

function handleSetAuthType(state, { data }) {
  return { ...state, authType: { basicAuth: data.basicAuth, type: data.authType, url: data.url } }
}

function handleSetMfaSecret(state, { qrCode, secret }) {
  return { ...state, mfaSecret: secret, qrCode }
}

function handleResetMfa(state) {
  return { ...state, mfaSecret: null, qrCode: null, currentDeviceId: null }
}

function handleAddMfaDevice(state, { device, qrCode }) {
  const { id: currentDeviceId, secret: mfaSecret, deviceName: newDeviceName } = device
  return { ...state, currentDeviceId, mfaSecret, qrCode, newDeviceName }
}

function handleNavigate(state, action) {
  if (!chain(action).get("location.pathname", null).endsWith("home/profile").value()) {
    return { ...state, ...DEFAULT_STATE }
  }

  return state
}

function handleSetNewDeviceName(state, { newDeviceName }) {
  return { ...state, newDeviceName }
}

export default createReducer(DEFAULT_STATE, {
  [UpdaterTypes.UPDATE_ERROR]: handleUpdateError,
  [UpdaterTypes.UPDATE_SUCCESS]: handleUpdateSuccess,
  [Types.SET_AUTH_TYPE]: handleSetAuthType,
  [Types.SET_MFA_SECRET]: handleSetMfaSecret,
  [Types.RESET_MFA]: handleResetMfa,
  [Types.ADD_MFA_DEVICE]: handleAddMfaDevice,
  [Types.SET_NEW_DEVICE_NAME]: handleSetNewDeviceName,
  [HistoryTypes.NAVIGATE]: handleNavigate,
})

function* newMfaDevice({ token }) {
  try {
    const { device, qrCode } = yield call(apiPost, "/api/users/mfa-device", { token })
    const { mfaDevices } = yield select(state => ({
      mfaDevices: state.user.mfaDevices,
    }))
    yield put(updateUser({ mfaDevices: [...mfaDevices, omit(device, ["secret"])] }))
    yield put(Creators.addMfaDevice(device, qrCode))
  } catch (err) {
    let message = "An error occured while verifying the MFA device."
    if (err.errors) {
      message = err.errors
    }
    yield put(notificationCreators.add("error", null, message))
  }
}

function* verifyMfaDevice({ deviceName, token }) {
  try {
    const { mfaDevices, currentDeviceId: deviceId } = yield select(state => ({
      mfaDevices: state.user.mfaDevices,
      currentDeviceId: getCurrentDeviceId(state),
    }))
    yield call(apiPatch, `/api/users/mfa-device/${deviceId}/verify`, { deviceName, token })

    yield put(updateUser({
      mfaEnabled: true,
      mfaDevices: mfaDevices.map(device => {
        if (device.id === deviceId) {
          return { ...device, deviceName, verified: true }
        } else {
          return { ...device }
        }
      }) }))
    yield put(Creators.resetMfa())
    yield put(notificationCreators.add("success", null, "MFA device verified successfully."))
  } catch (err) {
    let message = "An error occured while verifying the MFA device."
    if (err.errors) {
      message = err.errors
    }
    yield put(notificationCreators.add("error", null, message))
  }
}

function* deleteMfaDevice({ deviceId, token }) {
  try {
    const { mfaDevices } = yield select(state => ({
      mfaDevices: state.user.mfaDevices,
    }))

    // Are we removing the last verified MFA device, which would disable MFA
    const mfaEnabled = some(mfaDevices, ({ verified, id }) => verified && id !== deviceId)

    yield call(apiDelete, `/api/users/mfa-device/${deviceId}`, { token })
    yield put(updateUser({ mfaEnabled, mfaDevices: filter(mfaDevices, device => device.id !== deviceId) }))
  } catch (err) {
    let message = "An error occured while removing the MFA device."
    if (err.errors) {
      message = err.errors
    }
    yield put(notificationCreators.add("error", null, message))
  }
}

function* saveMfaDeviceName({ name, deviceId }) {
  try {
    const { mfaDevices } = yield select(state => ({
      mfaDevices: state.user.mfaDevices,
    }))

    yield call(apiPatch, `/api/users/mfa-device/${deviceId}`, { name }, true)
    yield put(updateUser({ mfaDevices: mfaDevices.map(device => {
      if (device.id === deviceId) {
        return { ...device, deviceName: name }
      } else {
        return { ...device }
      }
    }) }))
  } catch (err) {
    let message = "An error occured while changing the MFA device name."
    if (err.errors) {
      message = err.errors
    }
    yield put(notificationCreators.add("error", null, message))
  }
}

export function* saga() {
  yield takeLatest(Types.NEW_MFA_DEVICE, newMfaDevice)
  yield takeLatest(Types.VERIFY_MFA_DEVICE, verifyMfaDevice)
  yield takeLatest(Types.DELETE_MFA_DEVICE, deleteMfaDevice)
  yield takeLatest(Types.SAVE_MFA_DEVICE_NAME, saveMfaDeviceName)
}
